import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import { DocumentUpload } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchSelectOptions, type SelectOption } from '../api/client';
import { can, resourcePermissions } from '../auth/permissions';
import { useAuth } from '../components/AuthGate';
import { CrudResourcePage } from '../crud/CrudResourcePage';
import { resourceConfigByName } from '../crud/resourceConfigs';
import { t } from '../i18n';

type ImportSummary = {
  rows: number;
  created: number;
  skipped: number;
  foreign_keys_cleared: number;
  portions?: number;
  portion_items?: number;
  portion_essentials?: number;
  portion_choosables?: number;
  portion_choosable_items?: number;
  combo_rows_skipped?: number;
  errors: string[];
};

export function ItemsPage() {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const canImport = can(user, resourcePermissions('items').create);

  useEffect(() => {
    if (!dialogOpen || categories.length) return;
    void fetchSelectOptions('all_categories/search').then(setCategories).catch(() => setCategories([]));
  }, [categories.length, dialogOpen]);

  const closeDialog = () => {
    if (importing) return;
    setDialogOpen(false);
    setFile(null);
    setCategoryId('');
  };

  const importSql = async () => {
    if (!file || !categoryId) return;
    setImporting(true);
    setSummary(null);
    try {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('category_id', categoryId);
      const response = await adminApi.post<{ data: ImportSummary }>('items/import-sql', payload);
      setSummary(response.data.data);
      setReloadKey((value) => value + 1);
      setToast({ message: t('itemImport.success', { count: response.data.data.created }), severity: 'success' });
    } catch (error) {
      setToast({ message: apiErrorMessage(error, t('itemImport.failed')), severity: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Stack spacing={2}>
      {canImport ? (
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" startIcon={<DocumentUpload size={18} />} onClick={() => setDialogOpen(true)}>
            {t('itemImport.button')}
          </Button>
        </Stack>
      ) : null}

      <CrudResourcePage key={reloadKey} config={resourceConfigByName('items')!} />

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('itemImport.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('itemImport.description')}</Alert>
            <Button variant="outlined" component="label" startIcon={<DocumentUpload size={18} />}>
              {file ? file.name : t('itemImport.chooseFile')}
              <input
                hidden
                type="file"
                accept=".sql,.txt,text/plain,application/sql"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </Button>
            <FormControl fullWidth required>
              <InputLabel>{t('itemImport.category')}</InputLabel>
              <Select value={categoryId} label={t('itemImport.category')} onChange={(event) => setCategoryId(String(event.target.value))}>
                {categories.map((category) => (
                  <MenuItem key={category.value} value={String(category.value)}>{category.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">{t('itemImport.categoryHelp')}</Typography>
            {summary ? (
              <Alert severity={summary.errors.length ? 'warning' : 'success'}>
                {t('itemImport.summary', {
                  rows: summary.rows,
                  created: summary.created,
                  skipped: summary.skipped,
                  cleared: summary.foreign_keys_cleared
                })}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
                  Combo rows: {summary.portions ?? 0} portions, {summary.portion_essentials ?? 0} essential items, {summary.portion_choosables ?? 0} choice groups, {summary.portion_choosable_items ?? 0} choice items, {summary.portion_items ?? 0} recipe items, {summary.combo_rows_skipped ?? 0} skipped.
                </Typography>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={importing}>{t('action.cancel')}</Button>
          <Button variant="contained" onClick={() => void importSql()} disabled={importing || !file || !categoryId}>
            {importing ? t('itemImport.importing') : t('itemImport.import')}
          </Button>
        </DialogActions>
      </Dialog>

      {toast ? (
        <Snackbar open autoHideDuration={5000} onClose={() => setToast(null)}>
          <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}
